import type { ReactNode } from 'react';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { Button } from '@src/components/Button/Button';
import { Cluster } from '@src/components/Cluster/Cluster';
import { Menu, type MenuItem } from '@src/components/Menu/Menu';
import { PencilIcon } from '@src/components/icons/PencilIcon';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './QuickDrawHero.css';

export interface QuickDrawHeroProps {
  name: string;
  picture?: string | null;
  logo?: string | null;
  lead: string;
  participantsLabel: string;
  participantsValue: string;
  invitedLabel: string;
  invitedValue: string;
  revealedLabel: string;
  revealedValue: string;
  onRename?: () => void;
  renameLabel?: string;
  renameDisabled?: boolean;
  archivedBadge?: ReactNode;
  menu?: MenuItem[];
  menuLabel?: string;
  testId?: string;
}

/** Sky hero for the quick-draw organizer dashboard — picture, title, stats. */
export function QuickDrawHero({
  name,
  picture,
  logo,
  lead,
  participantsLabel,
  participantsValue,
  invitedLabel,
  invitedValue,
  revealedLabel,
  revealedValue,
  onRename,
  renameLabel,
  renameDisabled = false,
  archivedBadge,
  menu,
  menuLabel,
  testId,
}: QuickDrawHeroProps) {
  return (
    <header className="qd-hero" {...testIdAttr(testId)}>
      {menu && menu.length > 0 ? (
        <div className="qd-hero__menu">
          <Menu
            items={menu}
            label={menuLabel ?? 'More'}
            align="end"
            testId={testIdFor(testId, 'menu')}
          />
        </div>
      ) : null}
      <div className="qd-hero__art-slot">
        <div className="qd-hero__art" {...testIdAttr(testIdFor(testId, 'art'))}>
          <BoxPicture picture={picture} logo={logo} />
        </div>
      </div>
      <div className="qd-hero__main">
        <div className="qd-hero__copy">
          {archivedBadge ? (
            <Cluster gap={2} align="center" className="qd-hero__badges">
              {archivedBadge}
            </Cluster>
          ) : null}
          <div className="qd-hero__title-row">
            <HeroTitle
              name={name}
              onRename={onRename}
              renameLabel={renameLabel}
              renameDisabled={renameDisabled}
              testId={testId}
            />
          </div>
          <Typography
            as="p"
            role="body/lead"
            className="qd-hero__lead"
            {...testIdAttr(testIdFor(testId, 'lead'))}
          >
            {lead}
          </Typography>
        </div>
        <div className="qd-hero__stats" {...testIdAttr(testIdFor(testId, 'stats'))}>
          <div className="qd-hero__stats-group">
            <Stat
              label={participantsLabel}
              value={participantsValue}
              testId={testIdFor(testId, 'stat-participants')}
            />
            <Stat
              label={invitedLabel}
              value={invitedValue}
              tone="info"
              testId={testIdFor(testId, 'stat-invited')}
            />
            <Stat
              label={revealedLabel}
              value={revealedValue}
              tone="success"
              testId={testIdFor(testId, 'stat-revealed')}
            />
          </div>
        </div>
      </div>
    </header>
  );
}

function HeroTitle({
  name,
  onRename,
  renameLabel,
  renameDisabled,
  testId,
}: {
  name: string;
  onRename?: () => void;
  renameLabel?: string;
  renameDisabled?: boolean;
  testId?: string;
}) {
  const canRename = Boolean(onRename) && !renameDisabled;

  if (!canRename) {
    return (
      <Typography as="h1" role="title/page" className="qd-hero__title" {...testIdAttr(testIdFor(testId, 'title'))}>
        {name}
      </Typography>
    );
  }

  return (
    <>
      <Typography
        as="h1"
        role="title/page"
        className="qd-hero__title qd-hero__title--desktop"
        {...testIdAttr(testIdFor(testId, 'title'))}
      >
        {name}
      </Typography>
      <Typography
        as="button"
        role="title/page"
        className="qd-hero__title qd-hero__title--mobile qd-hero__title--rename"
        type="button"
        aria-label={renameLabel ? `${name}. ${renameLabel}` : name}
        onClick={onRename}
        {...testIdAttr(testIdFor(testId, 'title'))}
      >
        {name}
      </Typography>
      <Button
        variant="ghost"
        size="small"
        className="qd-hero__rename"
        icon={<PencilIcon />}
        aria-label={renameLabel}
        title={renameLabel}
        onClick={onRename}
        testId={testIdFor(testId, 'rename')}
      />
    </>
  );
}

function Stat({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: string;
  tone?: 'info' | 'success' | 'support';
  testId?: string;
}) {
  const valueClass =
    tone === 'info'
      ? 'qd-hero__stat-value qd-hero__stat-value--info'
      : tone === 'success'
        ? 'qd-hero__stat-value qd-hero__stat-value--success'
        : tone === 'support'
          ? 'qd-hero__stat-value qd-hero__stat-value--support'
          : 'qd-hero__stat-value';

  return (
    <div className="qd-hero__stat" {...testIdAttr(testId)}>
      <span className={valueClass}>{value}</span>
      <Typography as="span" role="meta/helper">
        {label}
      </Typography>
    </div>
  );
}
